import { useEffect, useState, useRef } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  filteredAssignmentsAtom,
  assignmentsLoadingAtom,
  fetchAssignmentsActionAtom,
} from '../stores/assignmentsStore';
import { NewAssignmentDialog } from '../components/assignment/NewAssignmentDialog';
import {
  AssignmentHeader,
  AssignmentList,
  AssignmentEmptyState,
  AssignmentLoadingState,
} from '@/components/assignment';
import { fetchSourcesActionAtom, sourcesAtom } from '@/stores/sourcesStore';

export function Assignments() {
  const assignments = useAtomValue(filteredAssignmentsAtom);
  const isLoading = useAtomValue(assignmentsLoadingAtom);
  const fetchAssignments = useSetAtom(fetchAssignmentsActionAtom);
  const sources = useAtomValue(sourcesAtom);
  const fetchSources = useSetAtom(fetchSourcesActionAtom);
  const tips = useAtomValue(sourcesAtom);
  const fetchTips = useSetAtom(fetchSourcesActionAtom);

  const [activeTab, setActiveTab] = useState('all');
  const [expandedAssignments, setExpandedAssignments] = useState<Set<string>>(
    new Set()
  );
  const [isNewAssignmentOpen, setIsNewAssignmentOpen] = useState(false);

  // Fetch assignments once on mount
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchAssignments();
    if (sources.length === 0) {
      fetchSources();
    }
    if (tips.length === 0) {
      fetchTips();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAssignmentExpanded = (assignmentId: string) => {
    setExpandedAssignments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  };

  // Filter assignments by tab
  const getFilteredAssignmentsByTab = (tab: string) => {
    switch (tab) {
      case 'active':
        return assignments.filter(
          a => !['published', 'cancelled'].includes(a.status)
        );
      case 'drafts':
        return assignments.filter(a =>
          ['draft_generated', 'under_review'].includes(a.status)
        );
      case 'published':
        return assignments.filter(a => a.status === 'published');
      default:
        return assignments;
    }
  };

  const displayAssignments = getFilteredAssignmentsByTab(activeTab);

  return (
    <div className='h-full flex flex-col overflow-hidden'>
      <AssignmentHeader
        allAssignmentsCount={displayAssignments.length}
        displayAssignmentsCount={displayAssignments.length}
        activeTab={activeTab}
        onActiveTabChange={setActiveTab}
        onNewAssignment={() => setIsNewAssignmentOpen(true)}
      />

      <div className='flex-1 overflow-hidden'>
        {isLoading && displayAssignments.length === 0 ? (
          <div className='p-4'>
            <AssignmentLoadingState />
          </div>
        ) : displayAssignments.length === 0 ? (
          <div className='p-4'>
            <AssignmentEmptyState activeTab={activeTab} />
          </div>
        ) : (
          <AssignmentList
            assignments={displayAssignments}
            expandedAssignments={expandedAssignments}
            onToggleExpanded={toggleAssignmentExpanded}
          />
        )}
      </div>

      <NewAssignmentDialog
        open={isNewAssignmentOpen}
        onOpenChange={setIsNewAssignmentOpen}
      />
    </div>
  );
}
